import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { trackPromise } from "react-promise-tracker";
import { GetManageColumns, SaveManageColumns } from "redux/actions";
import toast from "utils/toast";

const useManageColumns = ({
  clientId,
  entityName,
  originalColumnDefs,
  setColumnDefs,
  gridApi,
  enableManageColumns = false,
  enableAutoSync = true,
}) => {
  const dispatch = useDispatch();
  const { manage_columns } = useSelector((state) => state.configuration);
  const syncTimeoutRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const hasLoadedSavedColumnsRef = useRef(false); // NEW: Track if we've loaded saved columns

  const isDisabled = !enableManageColumns || !clientId || !entityName;

  /**
   * Load saved column configuration from backend
   */
  useEffect(() => {
    if (isDisabled) return;

    trackPromise(dispatch(GetManageColumns(clientId, entityName)));
  }, [clientId, entityName, dispatch, isDisabled]);

//   useEffect(() => {
//     console.log("🔵 HOOK STATE:", {
//       isDisabled,
//       hasOriginalColumns: !!originalColumnDefs?.length,
//       hasManageColumnsData: !!manage_columns?.data,
//       hasLoadedSaved: hasLoadedSavedColumnsRef.current,
//       hasGridApi: !!gridApi,
//       isInitialLoad: isInitialLoadRef.current,
//     });
//   }, [isDisabled, originalColumnDefs, manage_columns, gridApi]);

  /**
   * Apply saved column configuration to grid
   */
  useEffect(() => {
    // console.log("🟡 APPLY COLUMNS useEffect triggered", {
    //   isDisabled,
    //   hasOriginalColumns: originalColumnDefs?.length,
    //   hasData: !!manage_columns?.data,
    //   alreadyLoaded: hasLoadedSavedColumnsRef.current,
    //   hasGridApi: !!gridApi,
    // });

    if (isDisabled || !originalColumnDefs?.length || !manage_columns?.data) {
    //   console.log("❌ Early return - missing requirements");
      return;
    }

    if (hasLoadedSavedColumnsRef.current) {
      //console.log("❌ Early return - already loaded");
      return;
    }

    if (!gridApi) {
      //console.log("❌ Early return - waiting for gridApi");
      return;
    }

    try {
      const savedColumns = JSON.parse(manage_columns.data);
      //console.log("📦 Saved columns from API:", savedColumns);

      const updatedColumns = originalColumnDefs.map((col) => {
        const savedCol = savedColumns.find(
          (sc) => sc.field === col.field && sc.headerName === col.headerName
        );

        if (savedCol) {
        //   console.log(`🔄 Applying saved config for ${col.field}:`, {
        //     originalHide: col.hide,
        //     savedHide: savedCol.hide,
        //   });
          return {
            ...col,
            hide: savedCol.hide ?? col.hide,
            order: savedCol.order ?? col.order,
            width: savedCol.width ?? col.width,
            pinned: savedCol.pinned ?? col.pinned,
          };
        }
        return col;
      });

    //   console.log(
    //     "✅ Final updated columns:",
    //     updatedColumns.map((c) => ({
    //       field: c.field,
    //       hide: c.hide,
    //     }))
    //   );

      const sortedColumns = updatedColumns.sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      );

      setColumnDefs(sortedColumns);
      hasLoadedSavedColumnsRef.current = true;

      requestAnimationFrame(() => {
        isInitialLoadRef.current = false;
        //console.log("🟢 Initial load complete - auto-sync enabled");
      });
    } catch (error) {
      console.error("💥 Error applying saved column configuration:", error);
      isInitialLoadRef.current = false;
    }
  }, [manage_columns, originalColumnDefs, setColumnDefs, isDisabled, gridApi]);

  /**
   * Save column configuration to backend
   */
  const saveColumnsToBackend = useCallback(
    async (columns, showToast = false) => {
      if (isDisabled || !columns?.length) return;

    //   console.log(
    //     "💾 SAVING COLUMNS:",
    //     columns.map((c) => ({
    //       field: c.field,
    //       hide: c.hide,
    //     }))
    //   );

      try {
        
       dispatch(SaveManageColumns(clientId, columns, entityName))

        // if (showToast) {
        //   toast.success("Column preferences saved successfully");
        // }
      } catch (error) {
        console.error("Error saving column configuration:", error);
        // if (showToast) {
        //   toast.error("Failed to save column preferences");
        // }
      }
    },
    [clientId, entityName, dispatch, isDisabled]
  );

  /**
   * Extract current column state from AG Grid
   */
  const extractColumnState = useCallback(() => {
    if (!gridApi) return null;

    const columnState = gridApi.getColumnState();

    // Filter only data columns (those with field property) and assign order based on position
    let dataColumnIndex = 0;
    const extractedColumns = [];

    columnState.forEach((state, index) => {
      // Find the column definition
      const allColumns = gridApi.getAllGridColumns();
      const col = allColumns.find(c => c.getColId() === state.colId);
      
      if (!col) return;
      
      const colDef = col.getColDef();
      
      // Only process columns that have a field (data columns)
      if (!colDef.field) return;
      
      const extractedCol = {
        field: colDef.field,
        headerName: colDef.headerName,
        hide: state.hide ?? false,
        order: dataColumnIndex, // Use position in data columns, not grid columns
        customOrder: dataColumnIndex,
        width: state.width ?? colDef.width,
        pinned: state.pinned ?? null,
        sort: state.sort ?? null,
        sortIndex: state.sortIndex ?? null,
      };

      extractedColumns.push(extractedCol);
      dataColumnIndex++;
    });

    return extractedColumns;
  }, [gridApi]);

  /**
   * Sync columns with debouncing
   */
  const syncColumnsDebounced = useCallback(
    (showToast = false) => {
      // IMPORTANT: Don't sync during initial load
      if (isDisabled || !enableAutoSync || isInitialLoadRef.current) return;

      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      syncTimeoutRef.current = setTimeout(() => {
        const columnState = extractColumnState();
        if (columnState) {
          saveColumnsToBackend(columnState, showToast);
        }
      }, 1000);
    },
    [isDisabled, enableAutoSync, extractColumnState, saveColumnsToBackend]
  );

  /**
   * Sync columns immediately (for explicit save actions)
   */
  const syncColumnsImmediate = useCallback(
    (showToast = true) => {
      if (isDisabled || !gridApi) return;

      const columnState = extractColumnState();
      if (columnState) {
        saveColumnsToBackend(columnState, showToast);
      }
    },
    [isDisabled, gridApi, extractColumnState, saveColumnsToBackend]
  );

  /**
   * Reset columns to default
   */
  const resetColumns = useCallback(() => {
    if (!originalColumnDefs?.length || !gridApi) return;

    //console.log("🔄 RESETTING COLUMNS to original");

    // Get clean original state
    const resetState = originalColumnDefs.map((col, index) => ({
      ...col,
      hide: col.hide ?? false,
      order: index,
      pinned: null,
    }));

    // console.log(
    //   "Original columns:",
    //   resetState.map((c) => ({ field: c.field, hide: c.hide }))
    // );

    // Apply to AG Grid directly
    gridApi.setGridOption("columnDefs", resetState);

    // Also update React state
    setColumnDefs([...resetState]);

    // Save to backend
    if (!isDisabled) {
      const columnsToSave = resetState.map((col, index) => ({
        field: col.field,
        headerName: col.headerName,
        hide: col.hide ?? false,
        order: index,
        width: col.width,
        pinned: null,
      }));

      saveColumnsToBackend(columnsToSave, true);
    }
  }, [
    gridApi,
    originalColumnDefs,
    setColumnDefs,
    saveColumnsToBackend,
    isDisabled,
  ]);

  /**
   * Setup AG Grid event listeners for auto-sync
   */
  useEffect(() => {
    if (isDisabled || !gridApi || !enableAutoSync) return;

    const onColumnChanged = (event) => {
      // Skip during initial load
      if (isInitialLoadRef.current) return;

      const syncEvents = [
        "columnMoved",
        "columnVisible",
        "columnPinned",
        "columnRowGroupChanged",
      ];

      if (syncEvents.includes(event.type)) {
        syncColumnsDebounced(false);
      }
    };

    const eventTypes = [
      "columnMoved",
      "columnVisible",
      "columnPinned",
      "columnRowGroupChanged",
    ];

    eventTypes.forEach((eventType) => {
      gridApi.addEventListener(eventType, onColumnChanged);
    });

    return () => {
      eventTypes.forEach((eventType) => {
        gridApi.removeEventListener(eventType, onColumnChanged);
      });

      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [gridApi, enableAutoSync, syncColumnsDebounced, isDisabled]);

  return {
    syncColumnsImmediate,
    resetColumns,
    extractColumnState,
    saveColumnsToBackend,
    isManageColumnsEnabled: enableManageColumns && !isDisabled,
  };
};

export default useManageColumns;
